const DEFAULT_ENDPOINT = "https://tealfabric.io/webpage-summarizer";
const DEFAULT_TARGET_SELECTOR = "[data-ai-summary]";

type SummarizerConfig = {
  endpoint: string;
  maxLength?: number;
  language?: string;
  url?: string;
  timeoutMs: number;
};

type ApiResponse = {
  result?: {
    summary?: string;
    length?: number;
    language?: string;
    url?: string;
  };
};

const styleId = "tealfabric-webpage-summarizer-style";

function injectStyles(): void {
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
  [data-ai-summary] {
    position: relative;
    min-height: 2.5rem;
  }

  .tf-summary-status {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    opacity: 0.78;
    font-size: 0.92em;
  }

  .tf-summary-spinner {
    width: 0.92rem;
    height: 0.92rem;
    border: 2px solid currentColor;
    border-top-color: transparent;
    border-radius: 50%;
    animation: tf-summary-spin 0.8s linear infinite;
  }

  @keyframes tf-summary-spin {
    to { transform: rotate(360deg); }
  }

  .tf-summary-meta {
    margin-top: 0.5rem;
    font-size: 0.82em;
    opacity: 0.68;
  }
  `;
  document.head.appendChild(style);
}

function getConfigFromElement(element: HTMLElement): SummarizerConfig {
  const endpoint = element.dataset.aiSummaryEndpoint ?? DEFAULT_ENDPOINT;
  const maxLength = element.dataset.aiSummaryMaxLength
    ? Number.parseInt(element.dataset.aiSummaryMaxLength, 10)
    : undefined;
  const language = element.dataset.aiSummaryLanguage || undefined;
  const url = element.dataset.aiSummaryUrl || undefined;
  const timeoutMs = element.dataset.aiSummaryTimeout
    ? Number.parseInt(element.dataset.aiSummaryTimeout, 10)
    : 15000;

  return { endpoint, maxLength, language, url, timeoutMs };
}

function setLoadingState(element: HTMLElement): void {
  element.innerHTML = `
    <div class="tf-summary-status" role="status" aria-live="polite">
      <span class="tf-summary-spinner" aria-hidden="true"></span>
      <span>Generating AI summary...</span>
    </div>
  `;
}

function setErrorState(element: HTMLElement, message: string): void {
  element.innerHTML = "";
  const alert = document.createElement("p");
  alert.setAttribute("role", "alert");
  alert.textContent = message;
  element.appendChild(alert);
}

function renderSummary(
  element: HTMLElement,
  data: Required<NonNullable<ApiResponse["result"]>>
): void {
  const safeSummary = data.summary || "No summary returned.";
  element.innerHTML = "";

  const summary = document.createElement("p");
  summary.textContent = safeSummary;

  const meta = document.createElement("p");
  meta.className = "tf-summary-meta";
  meta.textContent = `Length: ${data.length} bytes | Language: ${data.language} | Source: ${data.url}`;

  element.append(summary, meta);
}

async function fetchSummary(config: SummarizerConfig): Promise<ApiResponse["result"]> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), config.timeoutMs);

  const payload: Record<string, string | number> = {};
  if (typeof config.maxLength === "number" && Number.isFinite(config.maxLength)) {
    payload.max_length = config.maxLength;
  }
  if (config.language) payload.language = config.language;
  if (config.url) payload.url = config.url;

  const response = await fetch(config.endpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify(payload),
    signal: controller.signal
  });

  clearTimeout(timeoutId);

  if (!response.ok) {
    throw new Error(`Summary request failed with status ${response.status}.`);
  }

  const json = (await response.json()) as ApiResponse;
  return json.result;
}

export async function hydrateSummaryContainer(element: HTMLElement): Promise<void> {
  if (element.dataset.aiSummaryHydrated === "true") return;
  element.dataset.aiSummaryHydrated = "true";

  injectStyles();
  setLoadingState(element);

  const config = getConfigFromElement(element);

  try {
    const result = await fetchSummary(config);
    if (!result) {
      setErrorState(element, "Summary API did not return a result.");
      return;
    }

    renderSummary(element, {
      summary: result.summary ?? "",
      length: result.length ?? 0,
      language: result.language ?? "unknown",
      url: result.url ?? config.url ?? window.location.href
    });
  } catch (error) {
    const fallback = "Could not generate summary right now. Please try again later.";
    if (error instanceof Error && error.name === "AbortError") {
      setErrorState(element, "Summary request timed out.");
      return;
    }
    setErrorState(element, fallback);
  }
}

export async function init(options?: {
  selector?: string;
}): Promise<void> {
  const selector = options?.selector ?? DEFAULT_TARGET_SELECTOR;
  const elements = Array.from(document.querySelectorAll<HTMLElement>(selector));

  await Promise.all(elements.map((element) => hydrateSummaryContainer(element)));
}

declare global {
  interface Window {
    WebpageSummarizer?: {
      init: typeof init;
      hydrateSummaryContainer: typeof hydrateSummaryContainer;
    };
  }
}

window.WebpageSummarizer = {
  init,
  hydrateSummaryContainer
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    void init();
  });
} else {
  void init();
}
